<template>
  <div>
    <el-dialog
      :visible="value"
      :title="$t('News.NewsRead.Title')"
      class="read-dialog"
      @open="getList"
      @close="closeDialog"
      @closed="clearData"
    >
      <div class="household-select">
        <el-button
          round
          class="custom-button"
          @click="isShowHouseholdFrame = true"
        >
          <span v-if="Object.keys(currentHousehold).length">
            {{ currentHousehold.TabletNote }}
            <i class="el-icon-circle-close" @click.stop="clearHousehold"></i>
          </span>
          <span v-else>{{ $t('News.NewsRead.SelectHousehold') }}</span>
        </el-button>
      </div>
      <el-tabs v-model="activeReadTab" v-loading="isLoading">
        <el-tab-pane name="unread">
          <span slot="label">
            <el-badge :hidden="!unReadList.length" :value="unReadList.length" class="read-dialog__badge">
              {{ $t('News.NewsRead.UnreadTab') }}
            </el-badge>
          </span>
        </el-tab-pane>
        <el-tab-pane name="read">
          <span slot="label">
            <el-badge :hidden="!readList.length" :value="readList.length" class="read-dialog__badge">
              {{ $t('News.NewsRead.ReadTab') }}
            </el-badge>
          </span>
        </el-tab-pane>
        <el-table
          v-if="activeReadTab === 'unread'"
          :data="unReadList"
          row-key="id"
        >
          <!-- 此 table 若沒有row-key，會導致已讀table壞掉 -->
          <el-table-column type="index" width="50" />
          <el-table-column prop="TabletNote" :label="$t('News.NewsRead.TabletNote')" />
          <!-- 未讀不顯示以下資訊，但為了UI過渡順暢，仍然保留欄位 -->
          <el-table-column prop="Device" label="" width="120" />
          <el-table-column prop="Model" label="" width="160" />
          <el-table-column prop="ReadDate" label="" width="160" />
        </el-table>
        <el-table
          v-if="activeReadTab === 'read'"
          :data="readList"
          row-key="id"
          :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
        >
          <el-table-column type="index" :index="indexMethod" width="50" />
          <el-table-column prop="TabletNote" :label="$t('News.NewsRead.TabletNote')" />
          <el-table-column prop="Device" :label="$t('News.NewsRead.Device')" width="120" />
          <el-table-column prop="Model" :label="$t('News.NewsRead.Model')" width="160" />
          <el-table-column prop="ReadDate" :label="$t('News.NewsRead.ReadDate')" width="160" />
        </el-table>
      </el-tabs>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="closeDialog">{{ $t('General.OK') }}</el-button>
      </span>
    </el-dialog>
    <household-frame
      ref="householdFrame"
      :open.sync="isShowHouseholdFrame"
      @select-household="selectedHousehold"
    />
  </div>
</template>

<script>
import householdFrame from '@/components/HouseholdFrame'
import { formatDateTime } from '@/js/utils/index.js'
import { apiAnnouncement } from '@/api/Announcement'
import { getDeviceModel } from '@/js/models/appleDevice'

export default {
  components: {
    'household-frame': householdFrame
  },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    announcementId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      isShowHouseholdFrame: false,
      internalVisible: this.value,
      activeReadTab: 'unread',
      recordData: { Unread: [], Read: [] },
      currentHousehold: {}, // 當前選到的戶別
      isLoading: false
    }
  },
  computed: {
    unReadList () {
      const householdId = this.currentHousehold.TabletId
      if (householdId) {
        return this.recordData.Unread.filter(item => item.TabletId === householdId)
      } else {
        return this.recordData.Unread
      }
    },
    readList () {
      const householdId = this.currentHousehold.TabletId
      // 若有選戶別，則篩選
      const initialData = this.recordData.Read.filter(item => !householdId || item.TabletId === householdId)

      // 前端需以樹狀資料顯示，組織成父子結構
      let fatherIndex = 1
      const groupedData = Object.values(
        // 把戶別資料先用reduce轉為物件，TabletId當key，方便後續操作
        // 最後再用 Object.values 轉成陣列
        initialData.reduce((acc, { TabletId, TabletNote, ...details }) => {
          if (!acc[TabletId]) {
            acc[TabletId] = {
              fatherIndex: fatherIndex++,
              TabletId,
              TabletNote,
              Device: '',
              Model: '',
              ReadDate: '',
              children: []
            }
          }
          acc[TabletId].children.push({
            TabletId,
            TabletNote,
            ...details,
            Model: getDeviceModel(details.Model)
          })

          return acc
        }, {})
      )

      // 為每個節點賦值 nodeId，給 Element ui 索引用，才可以顯示正確的編號
      // id 需要從 1 開始，否則table第一個無法展開
      let currentId = 1
      const assignNodeId = nodes => {
        nodes.forEach(node => {
          node.id = currentId++
          if (Array.isArray(node.children)) {
            assignNodeId(node.children)
          }
        })
      }
      assignNodeId(groupedData)
      return groupedData
    },
    listChange () {
      // 輔助 watch 監聽用
      return {
        unReadList: this.unReadList,
        readList: this.readList
      }
    }
  },
  watch: {
    listChange: {
      handler (newValue, oldValue) {
        // 選擇戶別後，自動跳tab到有資料的那邊
        if (this.unReadList.length > 0) {
          this.activeReadTab = 'unread'
        } else if (this.readList.length > 0) {
          this.activeReadTab = 'read'
        } else {
          this.activeReadTab = 'unread'
        }
      },
      deep: true
    }
  },
  methods: {
    async getList () {
      try {
        if (!this.recordData.Unread && !this.recordData.Read) {
          // 只有從沒資料過渡到有資料時才顯示 loading
          this.isLoading = true
        }
        const res = await apiAnnouncement.getReadRecord(this.announcementId)
        this.recordData = res.data.Data
        // 放在computed中會每次渲染都一直重新format，所以放在取得資料後處理
        this.recordData.Read = this.recordData.Read.map(item => {
          return {
            ...item,
            ReadDate: formatDateTime(item.ReadDate)
          }
        })
      } catch (e) {
        // 不特別處理失敗情境，createAxiosInstance 會自動處理
      } finally {
        this.isLoading = false
      }
    },
    selectedHousehold (content) {
      this.currentHousehold = content
      this.isShowHouseholdFrame = false
    },
    indexMethod (index) {
      // 編號(index) 是使用戶別，而非裝置，所以不能直接用list的index
      const target = this.readList.find(item => item.id === index + 1)
      return target ? target.fatherIndex : ''
    },
    clearHousehold () {
      this.currentHousehold = {}
    },
    closeDialog () {
      this.$emit('input', false)
    },
    clearData () {
      this.clearHousehold()
      this.recordData = { Unread: [], Read: [] }
      this.activeReadTab = 'unread'
      this.$refs.householdFrame.clearHousehold()
    }
  }
}
</script>

<style lang="scss" scoped>
.read-dialog {
  overflow: hidden;
  .dialog-footer {
    display: flex;
    justify-content: center;
  }

  ::v-deep .el-table {
    @include basicTable();
    height: 100%;
  }

  ::v-deep .el-dialog {
    @include basicDialog();
    width: 60% !important;
    .el-dialog__body {
      padding-top: 12px !important;
    }
  }
  .device-table {
    width: 100%;
  }
  .read-dialog__badge {
    margin-right: 15px;
    ::v-deep .el-badge__content  {
      top: 13px;
      right: -3px;
    }
  }
  .household-select {
    width: 250px;
    margin-bottom: 1rem;
    i {
      margin-left: 0.5rem;
      pointer-events: auto;
    }
  }
}
</style>
